import React, { memo, useMemo, useState } from 'react';
import { useSlideoutUrl } from 'app/hooks/useSlideoutUrl';
import { MonthlyDateBadge } from './MonthlyDateBadge';
import { MonthlyItemIcons } from './MonthlyItemIcons';
import { MonthlyItemTitle } from './MonthlyItemTitle';
import { MonthlyEventInlineItem } from './MonthlyEventInlineItem';
import {
  getMonthlyBadgeVariant,
  isMonthlyEventPast
} from '../utils/getMonthlyBadgeVariant';
import {
  isMonthlyEventHighlightSource,
  isMonthlyEventItem,
  useMonthlyEventHighlight
} from '../utils/monthlyEventHighlight';
import { useDelayedTooltip } from '../hooks/useDelayedTooltip';
import { useMonthlyUpdateSuccessHighlight } from '../hooks/useMonthlyUpdateSuccessHighlight';
import type { MonthlyTimelineItem } from '../utils/types';
import styles from '../MonthlyRoadmap.module.scss';

interface MonthlyInlineItemProps {
  item: MonthlyTimelineItem;
  laneLabel: string;
  className?: string;
  isChild?: boolean;
}

export const MonthlyInlineItem = memo(
  ({ item, laneLabel, className, isChild = false }: MonthlyInlineItemProps) => {
    const isEvent = isMonthlyEventItem(item, laneLabel);

    if (isEvent && !isChild) {
      return (
        <MonthlyEventInlineItem
          item={item}
          laneLabel={laneLabel}
          className={className}
        />
      );
    }

    return (
      <MonthlyStandardInlineItem
        item={item}
        laneLabel={laneLabel}
        className={className}
        isChild={isChild}
      />
    );
  }
);

const MonthlyStandardInlineItem = memo(
  ({ item, laneLabel, className, isChild = false }: MonthlyInlineItemProps) => {
    const { openSlideout } = useSlideoutUrl();
    const [isDateClicked, setIsDateClicked] = useState(false);
    const tooltip = useDelayedTooltip();

    const { showHighlight } = useMonthlyEventHighlight(item, laneLabel);

    const { isCompleted } = useMemo(
      () => getMonthlyBadgeVariant(item, laneLabel),
      [item, laneLabel]
    );

    const isPastEvent = useMemo(() => {
      if (!isMonthlyEventItem(item, laneLabel)) {
        return false;
      }

      return isMonthlyEventPast(item);
    }, [item, laneLabel]);

    const showDisabledRow = isCompleted || isPastEvent;
    const isMainCard = !isChild;
    const isActiveMainCard = isMainCard && !showDisabledRow;
    const issueId = item.issueId || item.id;
    const isTemporarilyHighlighted = useMonthlyUpdateSuccessHighlight(
      issueId,
      item.releasePhaseId
    );

    const openDetail = () => {
      setIsDateClicked(false);

      if (item.rsdLink && item.rsdLink.trim()) {
        window.open(item.rsdLink, '_blank', 'noopener,noreferrer');
        return;
      }

      if (item.issueId) {
        openSlideout(item.issueId, undefined, item.releasePhaseId);
      }
    };

    const relatedHighlight =
      showHighlight && !isMonthlyEventHighlightSource(item);

    return (
      <div
        className={`${styles.monthlyInlineItem} ${
          isMainCard
            ? styles.monthlyInlineItemMainCard
            : styles.monthlyInlineItemChildCard
        } ${isActiveMainCard ? styles.monthlyInlineItemActiveMain : ''} ${
          showDisabledRow ? styles.monthlyInlineItemCompleted : ''
        } ${className || ''} ${
          relatedHighlight ? styles.monthlyInlineItemHighlighted : ''
        } ${
          isTemporarilyHighlighted
            ? styles.monthlyInlineItemUpdateHighlight
            : ''
        }`}
      >
        <div className={styles.monthlyInlineItemMain}>
          {!isChild && (
            <MonthlyDateBadge
              item={item}
              laneLabel={laneLabel}
              isDateClicked={isDateClicked}
              setIsDateClicked={setIsDateClicked}
              useCompletedStyle={showDisabledRow}
            />
          )}
          <div className={styles.monthlyInlineItemBody}>
            <MonthlyItemTitle
              title={item.title || 'Untitled'}
              projectType={item.projectType || item.type}
              releasePhase={item.releasePhase}
              releasePhaseId={item.releasePhaseId}
              releasePhases={item.releasePhases}
              laneLabel={laneLabel}
              isChild={isChild}
              issueId={issueId}
              onPress={openDetail}
              tooltip={tooltip}
            />
            {!isChild && (
              <MonthlyItemIcons
                item={item}
                laneLabel={laneLabel}
                tooltip={tooltip}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);
